import type { LoaderFunctionArgs, MetaFunction } from 'react-router'
import { useLoaderData, Link } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema, FAQSchema } from '~/components/structured-data'
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import { METIERS } from '~/lib/metiers-data.server'
import {
  ArrowLeft, ArrowRight, Briefcase, GraduationCap, Euro,
  Sparkles, Award, Wrench, HelpCircle, TrendingUp, Users,
} from 'lucide-react'

export function headers() { return STATIC_PAGE_HEADERS }

export async function loader({ params }: LoaderFunctionArgs) {
  const metier = METIERS.find((m) => m.slug === params.slug)
  if (!metier) throw new Response('Not Found', { status: 404 })

  const related = metier.relatedSlugs
    .map((slug) => METIERS.find((m) => m.slug === slug))
    .filter((m): m is NonNullable<typeof m> => !!m)

  return { metier, related }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data) return []
  const m = data.metier
  const year = new Date().getFullYear()
  return [
    { title: `Métier ${m.artisan} — formation, salaire, qualifications ${year} | AfficheServices` },
    { name: 'description', content: `Tout sur le métier de ${m.artisan} en ${year} : missions, formations (CAP, Bac Pro, BTS), salaire (${m.salaire.debutant} à ${m.salaire.experimente}), qualifications RGE, évolution de carrière.` },
    { tagName: 'link', rel: 'canonical', href: `https://www.afficheservices.fr/metier/${m.slug}` },
    { property: 'og:title', content: `Métier ${m.artisan} — formation, salaire ${year} | AfficheServices` },
    { property: 'og:description', content: `Tout sur le métier de ${m.artisan} : formations, salaire ${m.salaire.debutant}, qualifications RGE.` },
    { property: 'og:url', content: `https://www.afficheservices.fr/metier/${m.slug}` },
  ]
}

export default function MetierDetailPage() {
  const { metier, related } = useLoaderData<typeof loader>()
  const m = metier

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Métiers', url: 'https://www.afficheservices.fr/metier' },
        { name: m.name, url: `https://www.afficheservices.fr/metier/${m.slug}` },
      ]} />
      {m.faq.length > 0 && <FAQSchema items={m.faq.map((f) => ({ question: f.q, answer: f.a }))} />}

      {/* Hero */}
      <section className={`bg-gradient-to-br ${m.heroColor} px-4 py-12 text-white sm:py-16`}>
        <div className="mx-auto max-w-4xl">
          <Link to="/metier" className="inline-flex items-center gap-1 text-xs font-semibold text-white/70 hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5" /> Tous les métiers
          </Link>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/80">
            <Briefcase className="h-3 w-3" /> Fiche métier
          </div>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-4xl">
            Le métier de {m.artisan}
          </h1>
          <p className="mt-3 max-w-2xl text-base text-white/80">{m.shortDesc}</p>

          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl bg-white/10 p-4 ring-1 ring-white/20">
              <p className="text-xs font-semibold uppercase tracking-wide text-white/60">Salaire débutant</p>
              <p className="mt-1 text-lg font-bold text-white">{m.salaire.debutant}</p>
            </div>
            <div className="rounded-xl bg-white/10 p-4 ring-1 ring-white/20">
              <p className="text-xs font-semibold uppercase tracking-wide text-white/60">Expérimenté</p>
              <p className="mt-1 text-lg font-bold text-white">{m.salaire.experimente}</p>
            </div>
            <div className="rounded-xl bg-white/10 p-4 ring-1 ring-white/20">
              <p className="text-xs font-semibold uppercase tracking-wide text-white/60">Indépendant</p>
              <p className="mt-1 text-lg font-bold text-white">{m.salaire.independant}</p>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto grid max-w-5xl gap-8 px-4 py-12 lg:grid-cols-3 lg:px-6">
        {/* Main content */}
        <div className="space-y-12 lg:col-span-2">
          {/* Missions */}
          <section>
            <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900">
              <Briefcase className="h-5 w-5 text-primary" /> Missions principales
            </h2>
            <ul className="mt-4 space-y-2">
              {m.missions.map((mission, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" /> {mission}
                </li>
              ))}
            </ul>
          </section>

          {/* Formations */}
          <section>
            <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900">
              <GraduationCap className="h-5 w-5 text-primary" /> Formations
            </h2>
            <div className="mt-4 space-y-3">
              {m.formations.map((f, i) => (
                <div key={i} className="rounded-xl border border-border bg-white p-4">
                  <p className="text-sm font-semibold text-slate-900">{f.name}</p>
                  <div className="mt-1 flex gap-3 text-xs text-slate-500">
                    <span>Durée : {f.duration}</span>
                    <span>· Niveau : {f.level}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Qualités requises */}
          <section>
            <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900">
              <Sparkles className="h-5 w-5 text-primary" /> Qualités requises
            </h2>
            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {m.qualites.map((q, i) => (
                <li key={i} className="flex items-start gap-2 rounded-xl bg-slate-50 p-3 text-sm text-slate-700">
                  <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" /> {q}
                </li>
              ))}
            </ul>
          </section>

          {/* Certifications */}
          <section>
            <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900">
              <Award className="h-5 w-5 text-primary" /> Certifications & qualifications
            </h2>
            <ul className="mt-4 space-y-2">
              {m.certifications.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <Award className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> {c}
                </li>
              ))}
            </ul>
          </section>

          {/* Tâches quotidiennes */}
          <section>
            <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900">
              <Users className="h-5 w-5 text-primary" /> Une journée type
            </h2>
            <ul className="mt-4 space-y-2">
              {m.tachesQuotidiennes.map((t, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary">
                    {i + 1}
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </section>

          {/* Outils */}
          <section>
            <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900">
              <Wrench className="h-5 w-5 text-primary" /> Outils du métier
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {m.outils.map((o, i) => (
                <span key={i} className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                  {o}
                </span>
              ))}
            </div>
          </section>

          {/* Évolution de carrière */}
          <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
            <h2 className="flex items-center gap-2 text-lg font-bold text-emerald-900">
              <TrendingUp className="h-5 w-5" /> Évolution de carrière
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-emerald-800">{m.evolutionCarriere}</p>
          </section>

          {/* FAQ */}
          {m.faq.length > 0 && (
            <section>
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900">
                <HelpCircle className="h-5 w-5 text-primary" /> Questions fréquentes
              </h2>
              <div className="mt-4 space-y-3">
                {m.faq.map((item, i) => (
                  <details key={i} className="group rounded-xl border border-border bg-white p-4">
                    <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold text-slate-900">
                      {item.q}
                      <ArrowRight className="h-4 w-4 transition-transform group-open:rotate-90" />
                    </summary>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{item.a}</p>
                  </details>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Sidebar */}
        <aside>
          <div className="sticky top-20 space-y-4">
            {/* Tarif horaire card */}
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
              <p className="flex items-center gap-2 text-xs font-semibold text-amber-700">
                <Euro className="h-3.5 w-3.5" /> Tarif horaire moyen
              </p>
              <p className="mt-2 text-2xl font-bold text-amber-900">{m.salaire.hourly}</p>
              <p className="mt-1 text-xs text-amber-700">Tarif facturé au client, varie selon région et complexité.</p>
            </div>

            {/* Finding a pro CTA */}
            <div className="rounded-2xl border border-border bg-white p-6 shadow-md">
              <h3 className="text-sm font-bold text-slate-900">Besoin d'un {m.artisan} ?</h3>
              <p className="mt-1 text-xs text-slate-500">
                Recevez 3 devis gratuits d'artisans qualifiés près de chez vous.
              </p>
              <Link
                to="/devis-gratuit"
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white hover:bg-primary-hover"
              >
                Demander mes devis <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to={`/services/${m.slug}`}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Voir les {m.artisan}s
              </Link>
            </div>

            {/* Related métiers */}
            {related.length > 0 && (
              <div className="rounded-2xl border border-border bg-slate-50 p-5">
                <p className="text-xs font-semibold text-slate-600">Métiers liés</p>
                <ul className="mt-3 space-y-2">
                  {related.map((r) => (
                    <li key={r.slug}>
                      <Link
                        to={`/metier/${r.slug}`}
                        className="flex items-center justify-between text-sm text-slate-700 hover:text-primary"
                      >
                        {r.artisan.charAt(0).toUpperCase() + r.artisan.slice(1)}
                        <ArrowRight className="h-3 w-3" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </aside>
      </div>

      <Footer />
    </div>
  )
}
